import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Breadcrumbs,
  Link,
  Button,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Tooltip,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReusableTable from "../../../components/ReusableTable/ReusableTable";
import { CheckStatusChip } from "../../../components/CheckStatusChip/CheckStatusChip";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import InformationStepsCard from "../../../components/InformationStepsCard/InformationStepsCard";
import FilterBar from "../../../components/FilterNSearchBar/FilterNSearchBar";
import { statusFilterOptions } from "../../../utils/enums";
import {
  GET_CODE_SECURITY_FINDINGS,
  GET_BRANCHES,
  SET_BRANCH_SCOPE,
} from "../../../apis/organization-apis/CodeSecurity-apis/CodeSecurityApis";
import {
  TABLE_MIN_WIDTH_SX,
  FILTER_BAR_WRAP_SX,
  matchesFilter,
  includesQuery,
  countLabel,
  CODE_SECURITY_PATH,
} from "./codeSecurityShared";

const levelColor = (level) => {
  if (level === "confirmed") return "error";
  if (level === "unconfirmed") return "warning";
  return "default";
};

const KIND_FILTER_OPTIONS = [
  { id: "confirmed", label: "Real secret" },
  { id: "unconfirmed", label: "Possible secret" },
];

const commitLabel = (value) => {
  const text = String(value || "").trim();
  if (!text) return "—";
  const sha = text.match(/\b[0-9a-f]{7,40}\b/i);
  return sha ? sha[0].slice(0, 12) : "—";
};

const hiddenSecret = (value) => {
  const text = String(value || "").trim();
  if (!text || text === "-" || text === "—") return "••••••••";
  return text;
};

const CodeSecurityFindings = () => {
  const { repoId, branchId } = useParams();
  const theme = useTheme();
  const [snackbar, setSnackbar] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableFilters, setTableFilters] = useState({});

  const { data, loading } = useQuery(GET_CODE_SECURITY_FINDINGS, {
    variables: { branch_data_id: branchId },
    skip: !branchId,
    fetchPolicy: "network-only",
  });
  const { data: branchesData, refetch: refetchBranches } = useQuery(
    GET_BRANCHES,
    {
      variables: { repo_data_id: repoId },
      skip: !repoId,
      fetchPolicy: "network-only",
    }
  );
  const [setBranchScope, { loading: scopeLoading }] =
    useMutation(SET_BRANCH_SCOPE);

  const allFindings = data?.getCodeSecurityFindings?.data || [];
  const findings = useMemo(
    () =>
      allFindings.filter((row) => {
        const fileLabel = `${row.file_path || ""}${
          row.line != null && row.line !== "" ? `:${row.line}` : ""
        }`;
        return (
          includesQuery(
            searchQuery,
            row.detector_name,
            fileLabel,
            row.redacted_secret,
            row.commit
          ) &&
          matchesFilter(tableFilters.level, row.level) &&
          matchesFilter(
            tableFilters.status,
            String(row.status || "").toLowerCase()
          )
        );
      }),
    [allFindings, searchQuery, tableFilters]
  );
  const branch = (branchesData?.getBranches?.data || []).find(
    (row) => String(row.id) === String(branchId)
  );
  const heading =
    branch?.branch || findings[0]?.resource_name || "Findings";
  const repoName = branch ? `${branch.owner}/${branch.repo}` : "Repository";
  const branchInScope = branch?.in_scope === true;

  const columns = useMemo(
    () => [
      { label: "What we found", field: "detector_name", width: "18%", align: "left" },
      { label: "File", field: "location", width: "28%", align: "left" },
      { label: "Secret (hidden)", field: "redacted_secret", width: "16%", align: "left" },
      { label: "Commit", field: "commit", width: "10%", align: "left" },
      {
        label: "Kind",
        field: "level",
        width: "14%",
        align: "left",
        filterConfig: { filterOptions: KIND_FILTER_OPTIONS },
      },
      {
        label: "Status",
        field: "status",
        width: "14%",
        align: "left",
        filterConfig: { filterOptions: statusFilterOptions },
      },
    ],
    []
  );

  const clearFilters = () => {
    setSearchQuery("");
    setTableFilters({});
  };

  const handleBranchScope = async (next) => {
    const result = await setBranchScope({
      variables: { branch_data_id: branchId, in_scope: next },
    });
    const payload = result?.data?.setBranchScope;
    setSnackbar({
      message: payload?.message || "Updated",
      variant: payload?.success ? "success" : "error",
    });
    await refetchBranches();
  };

  const renderCell = (row, column) => {
    switch (column.field) {
      case "location": {
        const label = `${row.file_path || "?"}${
          row.line != null && row.line !== "" ? `:${row.line}` : ""
        }`;
        if (row.file_url) {
          return (
            <Tooltip title="Open this file in the repository" arrow>
              <Link
                href={row.file_url}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                onClick={(event) => event.stopPropagation()}
                sx={{
                  display: "inline-flex",
                  alignItems: "flex-start",
                  gap: 0.5,
                  fontFamily: "monospace",
                  fontSize: "0.8125rem",
                  wordBreak: "break-all",
                  whiteSpace: "normal",
                  lineHeight: 1.35,
                }}
              >
                {label}
                <OpenInNewIcon sx={{ fontSize: 14, flexShrink: 0, mt: "1px" }} />
              </Link>
            </Tooltip>
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              wordBreak: "break-all",
              whiteSpace: "normal",
            }}
          >
            {label}
          </Typography>
        );
      }
      case "level":
        return (
          <Chip
            size="small"
            variant="outlined"
            color={levelColor(row.level)}
            label={row.level === "confirmed" ? "Real secret" : "Possible secret"}
          />
        );
      case "redacted_secret":
        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {hiddenSecret(row.redacted_secret)}
          </Typography>
        );
      case "commit":
        return (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {commitLabel(row.commit)}
          </Typography>
        );
      case "status":
        return (
          <CheckStatusChip status={String(row.status || "").toLowerCase()} />
        );
      default:
        return (
          <Typography variant="body2">
            {row[column.field] || "—"}
          </Typography>
        );
    }
  };

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1.5 }}>
        <Link
          component={RouterLink}
          to={CODE_SECURITY_PATH}
          underline="hover"
          color="text.secondary"
        >
          Code Security
        </Link>
        <Link
          component={RouterLink}
          to={`${CODE_SECURITY_PATH}/${repoId}`}
          underline="hover"
          color="text.secondary"
        >
          {repoName}
        </Link>
        <Typography color="text.primary">{heading}</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 1.5 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="Each row is one finding we found in this branch."
          steps={[
            ...(branch?.is_default_branch
              ? [
                  "The **default branch is always monitored** while this repository is in scope. You cannot remove it.",
                ]
              : [
                  "Use **Remove from scans** at the bottom to stop monitoring this branch. Existing findings are kept.",
                  "If this branch is out of scope, use **Add to scans** to include it next time.",
                ]),
            "Check the file and commit for each finding so you know where it came from.",
            "**Real secret** means we are sure this is a password, API key, or token. **Possible secret** means it looks like one, but we are not sure yet.",
          ]}
          footerText="💡 We never store the real secret — only a hidden preview."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors.text.strong}
              >
                Secrets found
              </Typography>
              {(searchQuery ||
                (tableFilters.level && tableFilters.level.length > 0) ||
                (tableFilters.status && tableFilters.status.length > 0)) && (
                <Chip
                  label="Filtered"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: theme.customColors.text.secondary,
                    borderColor: theme.customColors.border.main,
                    background: theme.customColors.background.light,
                  }}
                />
              )}
            </Box>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            padding: "12px 14px",
          }}
        />
        <CardContent sx={{ p: 0 }}>
          <Box sx={FILTER_BAR_WRAP_SX}>
            <FilterBar
              searchQuery={searchQuery}
              onSearchChange={(event) => setSearchQuery(event.target.value)}
              filters={[]}
              selectedFilter={null}
              onFilterChange={() => {}}
              onClearFilters={clearFilters}
              countLabel={countLabel(findings.length, "secret")}
            />
          </Box>
          <Divider sx={{ borderColor: "#E8EAED" }} />
          <Box sx={TABLE_MIN_WIDTH_SX}>
            <ReusableTable
              columns={columns}
              data={findings}
              loading={loading}
              renderCell={renderCell}
              onFilterChange={setTableFilters}
              filters={tableFilters}
              height="auto"
              getCellSx={(_row, column) => ({
                verticalAlign: "top",
                ...(column.field === "location"
                  ? { overflow: "visible", whiteSpace: "normal" }
                  : {}),
              })}
              emptyState={
                <Typography variant="body1" color="text.secondary">
                  {allFindings.length === 0
                    ? "No secrets found on this branch."
                    : "No secrets match your search."}
                </Typography>
              }
            />
          </Box>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
          p: 1.5,
          mt: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          flexWrap: "nowrap",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.customColors.text.disabled,
            fontStyle: "italic",
            fontSize: "12px",
            flex: 1,
            minWidth: 0,
          }}
        >
          {branch?.is_default_branch
            ? "The default branch is always monitored while this repository is in scope."
            : branchInScope
              ? "Remove this branch from scans to stop monitoring it. Existing findings are kept."
              : "Add this branch to scans so we look for leaked secrets here."}
        </Typography>
        {branch?.is_default_branch ? null : branchInScope ? (
          <Button
            variant="outlined"
            startIcon={<RemoveCircleOutlineIcon />}
            onClick={() => handleBranchScope(false)}
            disabled={scopeLoading}
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              borderColor: theme.customColors.status.error.main,
              color: theme.customColors.status.error.main,
              borderWidth: "2px",
              flexShrink: 0,
              "&:hover": {
                borderColor: theme.customColors.status.error.text,
                backgroundColor: theme.customColors.status.error.bg,
                color: theme.customColors.status.error.text,
              },
            }}
          >
            {scopeLoading ? "Removing..." : "Remove from scans"}
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => handleBranchScope(true)}
            disabled={scopeLoading}
            sx={{ flexShrink: 0 }}
          >
            {scopeLoading ? "Adding..." : "Add to scans"}
          </Button>
        )}
      </Box>

      {snackbar && (
        <ConfirmSnackbar
          snackbarInfo={snackbar}
          setSnackbarInfo={setSnackbar}
        />
      )}
    </Box>
  );
};

export default CodeSecurityFindings;
